import { useEffect, useState } from "react";
import { errorText } from "../../api/client";
import { pushBlock, pushPrefs, sendTestPush, setPushPrefs } from "../../pwa/push";
import type { PushBlock } from "../../pwa/push";
import type { PushPrefs } from "../../shared/types";
import { haptic } from "../haptics";
import { SectionTitle } from "./SectionTitle";

/** Why the switches are off and cannot be turned on, in words a grown-up can act on. */
const BLOCKED: Record<PushBlock, string> = {
  "install-ios":
    "On iPhone and iPad, add Pointz to your Home Screen first: tap Share, then Add to Home Screen, and open Pointz from there.",
  unsupported: "This browser can't show notifications. Try Pointz in Chrome, Edge, Firefox or Safari.",
  denied: "Notifications are blocked for Pointz. Allow them in this browser's or phone's settings, then come back.",
  "not-production": "Notifications only work in the live app, not on a preview or on localhost.",
};

const SWITCHES: { key: keyof PushPrefs; label: string }[] = [
  { key: "taps", label: "Points as they're added or removed" },
  { key: "weekly", label: "Weekly summary on Sunday evening" },
];

const OFF: PushPrefs = { taps: false, weekly: false };

/** Settings' Notifications section, grown-ups only: which pushes this browser gets. */
export function NotificationsSection() {
  // Read once: none of these change while Settings is open, except permission, which
  // the switches themselves ask for.
  const [block, setBlock] = useState<PushBlock | null>(() => pushBlock());
  const [prefs, setPrefs] = useState<PushPrefs | null>(block ? OFF : null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tested, setTested] = useState<string | null>(null);

  useEffect(() => {
    if (block) return;
    let live = true;
    pushPrefs()
      .then((p) => live && setPrefs(p))
      .catch(() => live && setPrefs(OFF));
    return () => {
      live = false;
    };
  }, [block]);

  async function toggle(key: keyof PushPrefs) {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    haptic();
    setBusy(true);
    setErr(null);
    try {
      await setPushPrefs(next);
      setPrefs(next);
    } catch (e) {
      // A "Block" answer to the permission prompt is a lasting state, so show it as one.
      setBlock(pushBlock());
      setErr(errorText(e, "Couldn't change that. Try again."));
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    haptic();
    setBusy(true);
    setErr(null);
    setTested(null);
    try {
      setTested(await sendTestPush());
    } catch (e) {
      setErr(errorText(e, "Couldn't send a test. Try again."));
    } finally {
      setBusy(false);
    }
  }

  const anyOn = prefs !== null && (prefs.taps || prefs.weekly);
  return (
    <section className="section">
      <SectionTitle emoji="🔔">Notifications</SectionTitle>
      <div className="list">
        {SWITCHES.map(({ key, label }) => (
          <div key={key} className="list__item notify">
            <span className="notify__label" id={`notify-${key}`}>
              {label}
            </span>
            <button
              className="switch"
              type="button"
              role="switch"
              aria-checked={prefs?.[key] ?? false}
              aria-labelledby={`notify-${key}`}
              disabled={block !== null || prefs === null || busy}
              onClick={() => void toggle(key)}
            >
              <span className="switch__thumb" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      {block ? (
        <p className="hint">{BLOCKED[block]}</p>
      ) : (
        <p className="hint">
          Sent to this phone only. A tap made on this phone is never announced back to it, and taps made between 9pm and
          7am arrive together at 7am. Every grown-up turns these on for their own phone.
        </p>
      )}
      {anyOn && !block && (
        <button className="button button--small notify__test" type="button" disabled={busy} onClick={() => void test()}>
          Send a test notification
        </button>
      )}
      {tested && !err && (
        <p className="hint" role="status">
          {tested}
        </p>
      )}
      {err && !block && <p className="error">{err}</p>}
    </section>
  );
}
